import React from 'react';
import PropTypes from 'prop-types';

const HistoryGraph = ({ data, title, color = '#3498db' }) => {
    if (!data || data.length === 0) {
        return (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                No data for this mode yet.
            </div>
        );
    }

    const margin = { top: 20, right: 20, bottom: 40, left: 50 };
    const width = 600;
    const height = 300;
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Get times for the y-axis
    const times = data.map(d => d.time);
    const minTime = Math.min(...times) * 0.9;
    const maxTime = Math.max(...times) * 1.1;
    const timeRange = maxTime - minTime;

    // Helper to get X coordinate (index)
    const getX = (index) => (index / (data.length - 1 || 1)) * chartWidth;
    
    // Helper to get Y coordinate (time)
    const getY = (time) => chartHeight - ((time - minTime) / timeRange) * chartHeight;

    const points = data.map((d, i) => `${getX(i)},${getY(d.time)}`).join(' ');
    
    const BELT_COLORS = {
        yellow: '#f1c40f',
        green: '#2ecc71',
        blue: '#3498db',
        purple: '#9b59b6',
        brown: '#8B4513',
        black: '#ecf0f1',
    };
    
    const getPointColor = (d) => {
        if (d.belt && BELT_COLORS[d.belt]) return BELT_COLORS[d.belt];
        return color;
    };

    return (
        <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
            <h3 style={{ textAlign: 'center', color: '#ccc', fontSize: '14px', marginBottom: '10px' }}>{title}</h3>
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
                {/* Axes */}
                <line x1={margin.left} y1={margin.top} x2={margin.left} y2={height - margin.bottom} stroke="#444" strokeWidth="1" />
                <line x1={margin.left} y1={height - margin.bottom} x2={width - margin.right} y2={height - margin.bottom} stroke="#444" strokeWidth="1" />

                {/* Grid Lines (horizontal) */}
                {[0, 0.25, 0.5, 0.75, 1].map(v => {
                    const y = margin.top + (chartHeight * (1 - v));
                    const label = (minTime + v * timeRange).toFixed(2);
                    return (
                        <React.Fragment key={v}>
                            <line x1={margin.left} y1={y} x2={width - margin.right} y2={y} stroke="#222" strokeWidth="1" strokeDasharray="4" />
                            <text x={margin.left - 10} y={y + 5} fill="#666" fontSize="10" textAnchor="end">{label}s</text>
                        </React.Fragment>
                    );
                })}

                {/* Labels (X Axis - just start/end date) */}
                <text x={margin.left} y={height - margin.bottom + 20} fill="#666" fontSize="10" textAnchor="start">Older</text>
                <text x={width - margin.right} y={height - margin.bottom + 20} fill="#666" fontSize="10" textAnchor="end">Newer</text>

                {/* Line Path */}
                <g transform={`translate(${margin.left}, ${margin.top})`}>
                    <polyline
                        points={points}
                        fill="none"
                        stroke={color}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        opacity="0.3"
                    />
                    {/* Points */}
                    {data.map((d, i) => (
                        <circle
                            key={i}
                            cx={getX(i)}
                            cy={getY(d.time)}
                            r="4"
                            fill={getPointColor(d)}
                            stroke={getPointColor(d)}
                            strokeWidth="1"
                        >
                            <title>
                                {`${new Date(d.timestamp).toLocaleString()}\n`}
                                {`Total: ${d.time.toFixed(3)}s\n`}
                                {d.belt ? `Belt: ${d.belt.toUpperCase()}\n` : ''}
                                {d.firstShot ? `First: ${d.firstShot.toFixed(3)}s\n` : ''}
                                {d.avgSplit ? `Avg Split: ${d.avgSplit.toFixed(3)}s` : ''}
                            </title>
                        </circle>
                    ))}
                </g>
            </svg>
        </div>
    );
};

HistoryGraph.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        time: PropTypes.number.isRequired,
        timestamp: PropTypes.string.isRequired,
    })).isRequired,
    title: PropTypes.string.isRequired,
    color: PropTypes.string,
};

export default HistoryGraph;
